import {
    type FC,
    useCallback,
    useContext,
    useEffect,
    useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
    ActionButton,
    Button,
    ButtonColour,
    ButtonStyle,
    DialogType,
    LayerContext,
    useDialogCreate,
    useLayerRemove,
} from "@onshopfront/components";
import { Application } from "./_Bootstrap/Application";
import { useGlobalInitialiseEffect } from "./_Bootstrap/GlobalInitialisation";
import { useShallowShopfrontSelector, useShopfrontDispatch } from "./_Bootstrap/hooks";
import { history, store } from "./_Bootstrap/RoutingAndStore";
import { showDialog } from "./Actions/Dialogs/DialogActions";
import { REQUEST_REGISTER } from "./Actions/General/GeneralActionConstants";
import { focusGained, focusLost } from "./Actions/General/LayoutActions";
import { changeRegister } from "./Actions/General/LocationActions";
import { toggleTraining } from "./Actions/Information/InformationActions";
import Dialog from "./Common/Dialogs/Dialog";
import RegisterTakeover from "./Common/Dialogs/RegisterTakeover";
import { DatabaseBlocked } from "./Common/Misc/DatabaseBlocked";
import LocalPopupContainer from "./Common/Popups/LocalPopupContainer";
import PopupContainer from "./Common/Popups/PopupContainer";
import Body from "./Layout/Body";
import { ContentWrapper } from "./Layout/ContentWrapper";
import { ShopfrontGeneralMenu } from "./Layout/Header/Menu/ShopfrontGeneralMenu";
import { MissingPopupPermissionError } from "./Layout/MissingPermissionError";
import LocationSelector from "./Routes/Location/LocationSelector";
import { LoginScreen } from "./Routes/Login/LoginScreen";
import { fireEventType } from "./Utilities/Generic/ActionsHelper";
import { preventInputScroll } from "./Utilities/Generic/DOM";
import { getOption, isTrainingMode } from "./Utilities/Platform/Options/Options";
import { sendSocketMessage } from "./Workers/Socket/SocketAgent";
import { SOCKET_EVENTS } from "./Workers/Socket/SocketEvents";
import { localTimeMismatchWarning } from "./Workers/Sync/LocalTimeMismatchWarning";

let hasJustTakenRegisterControl = false;

export const setHasJustTakenRegisterControl = (): void => {
    hasJustTakenRegisterControl = true;
    setTimeout(() => {
        hasJustTakenRegisterControl = false;
    }, 300);
};

const Shopfront: FC = () => {
    const dispatch = useShopfrontDispatch();
    const {
        userId,
        firstVisit,
        outlet,
        kicked,
        inUse,
        isGlobal,
        register,
        isAdmin,
        userName,
        databaseBlocked,
        missingPermissions,
    } = useShallowShopfrontSelector(state => ({
        userId            : state.user.id,
        firstVisit        : state.information.firstVisit,
        outlet            : state.location.outlet,
        kicked            : state.location.kicked,
        inUse             : state.location.inUse,
        isGlobal          : state.location.isGlobal,
        register          : state.location.register,
        isAdmin           : state.user.id !== false ? state.user.isAdmin : false,
        userName          : state.user.id !== false ? state.user.name : null,
        databaseBlocked   : state.information.databaseBlocked,
        missingPermissions: state.customerDisplay.missingPermissions,
    }));

    const layerContext = useContext(LayerContext);
    const createDialog = useDialogCreate();
    const removeLayer = useLayerRemove();
    const navigate = useNavigate();

    const [ wasKicked, setWasKicked ] = useState<false | { user: string; register: string; }>(false);

    useGlobalInitialiseEffect();

    useEffect(() => {
        // Handle any global events
        if(typeof window === "undefined") {
            return;
        }

        const onFocusGained = () => {
            // Delay so that the FocusLost component has time to catch the click event
            setTimeout(() => {
                dispatch(focusGained());
            }, 50);
        };

        const onFocusLost = () => {
            if(!(document && document.activeElement && document.activeElement.tagName.toLowerCase() === "iframe")) {
                dispatch(focusLost());
            }
        };

        window.addEventListener("focus", onFocusGained);
        window.addEventListener("blur", onFocusLost);
        window.addEventListener("wheel", preventInputScroll, {
            passive: false,
        });

        return () => {
            window.removeEventListener("focus", onFocusGained);
            window.removeEventListener("blue", onFocusLost);
            window.removeEventListener("wheel", preventInputScroll);
        };
    }, [ dispatch ]);

    useEffect(() => {
        setWasKicked(kicked);
    }, [ kicked ]);

    useEffect(() => {
        localTimeMismatchWarning(getOption("utc_offset"));
    }, []);

    useEffect(() => {
        if(
            !(
                wasKicked === false &&
                kicked !== false &&
                userId &&
                !isAdmin &&
                outlet
            )
        ) {
            return;
        }

        const options = [{
            label   : "Choose Location",
            classes : [ "button-primary" ],
            callback: (): void => {
                dispatch(fireEventType(REQUEST_REGISTER));
            },
        }, {
            label   : "Take Back Control",
            classes : [ "button-primary" ],
            callback: (): void => {
                setHasJustTakenRegisterControl();
                dispatch(changeRegister(outlet as { id: string; name: string; }, {
                    id  : kicked.register,
                    name: getOption("register_name"),
                }, false));

                sendSocketMessage(SOCKET_EVENTS.KICK_REGISTER, {
                    register: kicked.register,
                    user    : userName,
                });
            },
        }];

        dispatch(showDialog(
            "Register Takeover",
            <RegisterTakeover />,
            "information",
            options,
            false,
            false
        ));
    }, [ dispatch, isAdmin, kicked, outlet, userId, userName, wasKicked ]);

    const displayRegisterInUse = useCallback((userName: string, register: { id: string; name: string; }) => {
        const content = (
            <>
                <p>This register is already in use, what would you like to do?</p>
            </>
        );

        const options = [{
            label   : "Choose Another Location",
            classes : [ "button-primary" ],
            callback: (): void => {
                dispatch(fireEventType(REQUEST_REGISTER));
            },
        }, {
            label   : "Take Control",
            classes : [ "button-primary" ],
            callback: (): void => {
                setHasJustTakenRegisterControl();
                sendSocketMessage(SOCKET_EVENTS.KICK_REGISTER, {
                    register: register.id,
                    user    : userName,
                });
            },
        }];

        dispatch(showDialog(
            "Register in Use",
            content,
            "question",
            options,
            false,
            false
        ));
    }, [ dispatch ]);

    useEffect(() => {
        if(!inUse || !userId || !userName || !register || isAdmin) {
            return;
        }

        if(hasJustTakenRegisterControl) {
            setTimeout(() => {
                if(!store.getState().location.inUse) {
                    return;
                }

                displayRegisterInUse(userName, register as { id: string; name: string; });
            }, 500);
        } else {
            displayRegisterInUse(userName, register as { id: string; name: string; });
        }
    }, [ inUse, userName, register, isAdmin, displayRegisterInUse, userId ]);

    useEffect(() => {
        return history.listen(() => {
            for(const content of layerContext.content) {
                layerContext.remove(content.id);
            }
        });
    }, [ layerContext ]);

    useEffect(() => {
        // When the page changes, we should check if we're currently in training
        let currentDialogId: undefined | string;
        const localHistory = Application.get("History");
        const callback = () => {
            if(isTrainingMode() && localHistory.location.pathname !== "/") {
                currentDialogId = createDialog({
                    type   : DialogType.Warning,
                    title  : "Leaving Training Mode",
                    content: "You're currently in training mode, pages outside of the sell screen don't support " +
                        "training mode, would you like to disable it?",
                    buttons: [
                        <Button
                            key="return"
                            style={ButtonStyle.Filled}
                            colour={ButtonColour.Blue}
                            onClick={() => {
                                if(currentDialogId) {
                                    removeLayer(currentDialogId);
                                    currentDialogId = undefined;
                                }

                                navigate("/");
                            }}
                        >
                            Return to Sell Screen
                        </Button>,
                        <ActionButton
                            key="disable"
                            onClick={() => {
                                if(currentDialogId) {
                                    removeLayer(currentDialogId);
                                    currentDialogId = undefined;
                                }

                                dispatch(toggleTraining());
                            }}
                        >
                            Disable Training Mode
                        </ActionButton>,
                    ],
                    blockClosing: true,
                });
            }
        };

        let currentPath = localHistory.location.pathname;

        if(currentPath !== "/") {
            callback();
        }

        const removeListener = localHistory.listen(() => {
            if(currentDialogId) {
                removeLayer(currentDialogId);
            }

            if(currentPath === "/" && localHistory.location.pathname !== currentPath) {
                callback();
            }

            currentPath = localHistory.location.pathname;
        });

        return () => {
            if(currentDialogId) {
                removeLayer(currentDialogId);
            }

            removeListener();
        };
    }, [ createDialog, dispatch, navigate, removeLayer ]);

    if(databaseBlocked.blocked) {
        return (
            <DatabaseBlocked certain={databaseBlocked.certain} />
        );
    }

    return (
        <div id="shopfront">
            <PopupContainer />
            {!(userId === false && firstVisit) && (
                <ContentWrapper
                    menuItems={<ShopfrontGeneralMenu />}
                >
                    <Body />
                    <LocalPopupContainer />
                </ContentWrapper>
            )}
            <div id="focus" />
            {outlet === null && !isGlobal && !kicked && (
                <LocationSelector />
            )}
            {userId === false && (
                <LoginScreen />
            )}
            <div id="print" />
            <Dialog />
            {missingPermissions && (
                <MissingPopupPermissionError />
            )}
        </div>
    );
};

export default Shopfront;
